/**
 * ErrorBoundary — catches render-phase exceptions in any descendant page.
 *
 * Closes [GAP-37 Phase 1 Vague 1.A]: pre-this drop, a single render crash
 * (hook throw, undefined access, type narrowing miss) blanked the entire
 * PWA — the customer mid-checkout saw an empty white tab with no recovery
 * path. The Suspense fallback only catches lazy-load failures, not render
 * exceptions, so the boundary is necessary.
 *
 * Strict Techno-Brutalisme — black/white/red. No emoji, no gradient.
 *
 * Reload + Home CTAs are the recovery surfaces. The Sentry hook is
 * a stub today: when we wire a real reporter we replace `reportError` and
 * the existing call-sites stay valid.
 */
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

function reportError(error: Error, info: ErrorInfo): void {
  // Sentry / Bugsnag wiring lands separately. For now we just keep the
  // crash visible in the console — devtools open shows the stack, customer
  // sees the fallback UI.
  // eslint-disable-next-line no-console
  console.error('[TapNpay] render crash caught by ErrorBoundary', error, info);
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, info);
  }

  private handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  private handleHome = () => {
    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        className="mx-auto flex min-h-screen max-w-[420px] flex-col items-center justify-center bg-surface px-md py-xl text-on-surface"
      >
        <span
          className="material-symbols-outlined text-[64px] text-error"
          style={{ fontVariationSettings: "'FILL' 1" }}
          aria-hidden="true"
        >
          report
        </span>
        <h1 className="font-h2 text-h2 mt-md text-center text-on-surface">
          Une erreur est survenue
        </h1>
        <p className="font-body-md text-body-md mt-sm text-center text-outline">
          La page n'a pas pu s'afficher. Réessaye, ou demande à un serveur de t'aider.
        </p>

        <div className="mt-xl w-full space-y-md">
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            className="flex h-12 w-full items-center justify-center rounded-lg border-technical bg-on-surface font-label-caps uppercase tracking-widest text-surface active:opacity-80"
          >
            Recharger la page
          </button>
          <button
            type="button"
            onClick={this.handleHome}
            data-testid="error-boundary-home"
            className="flex h-12 w-full items-center justify-center rounded-lg border-technical bg-transparent font-label-caps uppercase tracking-widest text-on-surface"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }
}
